import {
  EMPTY_BINNED_DATA_BUFFER,
  GeckoBinnedDataBuffer,
} from 'features/dataLayers/geckoBinnedDataBuffer'
import isEqual from 'lodash/isEqual'
import isNil from 'lodash/isNil'
import React, { useMemo } from 'react'
import useSWRImmutable from 'swr/immutable'
import { GeckoComponentDataFrame, GeckoComponentRect } from 'types'

import { useCdfDataFetcher } from './useCdfDataFetcher'

const useMultiCDFRawValues = (
  cdfs: GeckoComponentDataFrame[],
  cropSelection?: GeckoComponentRect
) => {
  const dataFetcher = useCdfDataFetcher()
  const multiFetcher = React.useCallback(() => {
    return Promise.all(
      cdfs.map(async (cdf) => {
        return {
          buffer:
            (await dataFetcher(cdf, cropSelection)) ?? EMPTY_BINNED_DATA_BUFFER,
          id: cdf.id,
          generation: cdf.generation,
        }
      })
    )
  }, [cdfs, cropSelection, dataFetcher])

  const { data, isLoading: isLoading } = useSWRImmutable(
    [...cdfs.map((cdf) => ({ id: cdf.id, generation: cdf.generation })), '2d'],
    multiFetcher,
    // There is no cheap way to compare CDF data to know if it has changed,
    // and SWR will attempt to use stableHash... which involves iterating over
    // all elements in the returned array. Very slow. For more information,
    // see: https://github.com/vercel/swr/issues/1719
    //
    // Instead, check if the cdf-id or generation has changed.
    {
      compare: (a, b) =>
        isEqual(
          a?.map(({ id, generation }) => ({ id, generation })),
          b?.map(({ id, generation }) => ({ id, generation }))
        ),
    }
  )

  return useMemo(
    () => ({
      buffers: data?.map((item) => item.buffer) ?? [],
      ids: data?.map((item) => item.id) ?? [],
      isLoading,
    }),
    [data, isLoading]
  )
}

// TODO: ARCH-201
export const useMultiCDFRaw2D = (
  cdfs: GeckoComponentDataFrame[],
  cropSelection?: GeckoComponentRect
) => {
  const rawValuesSwr = useMultiCDFRawValues(cdfs, cropSelection)

  const rawSortedArrs: Record<string, GeckoBinnedDataBuffer> =
    React.useMemo(() => {
      const sortedArrs: Record<string, GeckoBinnedDataBuffer> =
        Object.fromEntries(
          rawValuesSwr.ids
            .map((id, index) => {
              const cdfIndex = cdfs.findIndex((cdf) => id === cdf.id)
              if (cdfIndex === -1) {
                console.warn('CDF not found for id:', id)
                return undefined
              }
              return [cdfs[cdfIndex].id, rawValuesSwr.buffers[index]]
            })
            .filter(
              (entry): entry is [string, GeckoBinnedDataBuffer] => !isNil(entry)
            )
        )
      return sortedArrs
    }, [cdfs, rawValuesSwr.buffers, rawValuesSwr.ids])

  const rawValues2d: Record<string, number[][]> | null = React.useMemo(() => {
    if (!cdfs.length) {
      return {}
    }
    const rawValues = Object.fromEntries(
      Object.entries(rawSortedArrs).map(([id, buffer]) => {
        const cdf = cdfs.find((cdf) => cdf.id === id)

        const xBinCount = cdf?.data.binning.x_bin_count ?? 0
        const yBinCount = cdf?.data.binning.y_bin_count ?? 0
        const resultArr = new Array<number[]>(yBinCount)
        for (let i = 0; i < yBinCount; i += 1) {
          resultArr[i] = new Array<number>(xBinCount).fill(Number.NaN)
        }
        buffer.values.forEach((value, index) => {
          const yIdx = Math.floor(index / xBinCount)
          const xIdx = index % xBinCount
          resultArr[yIdx][xIdx] = value
        })
        return [id, resultArr]
      })
    )
    return rawValues
  }, [cdfs, rawSortedArrs])

  return { data: rawValues2d, isLoading: rawValuesSwr.isLoading }
}
